/**
 * Build a per-order shipping-label PDF preview from team attaches.
 * Source bytes: Smoke Drive (driveItemId) or IndexedDB ship-label-src-{attachId}.
 */

import { getDriveShareLink } from '../services/operatorDriveService';
import {
  SHIPPING_LABEL_ATTACH_STATUS,
  buildOrderKeyToPagesMap,
} from '../services/shippingLabelAttachService';
import { splitShippingLabelPdf } from '../services/shippingLabelPdfMatchService';
import { getPrintQueueBlob } from '../services/printQueueStore';
import { loadOperatorDriveItemBlob } from './operatorDriveShareDownload';
import { collectShipLabelOrderKeys } from './shippingLabelOrderStatus';
import { getWaveOrderKey } from './wavePickListModel';

export function shipLabelSourceBlobRef(attachId) {
  return `ship-label-src-${String(attachId || '').trim()}`;
}

/**
 * Attaches that mention any identity key for this order.
 * @param {object} order
 * @param {object[]} attaches
 */
export function findShippingLabelAttachesForOrder(order, attaches = []) {
  const keys = new Set(collectShipLabelOrderKeys(order));
  const waveKey = getWaveOrderKey(order);
  if (waveKey) keys.add(waveKey);
  if (!keys.size) return [];
  return (attaches || []).filter((attach) => {
    if (
      attach?.status !== SHIPPING_LABEL_ATTACH_STATUS.ATTACHED &&
      attach?.status !== SHIPPING_LABEL_ATTACH_STATUS.PENDING_REVIEW
    ) {
      return false;
    }
    return (attach.pages || []).some((page) =>
      keys.has(String(page?.orderKey || '').replace(/^#/, '').trim()),
    );
  });
}

async function loadAttachSourceBlob(attach) {
  if (attach?.id) {
    const cached = await getPrintQueueBlob(shipLabelSourceBlobRef(attach.id));
    if (cached instanceof Blob && cached.size) return cached;
  }
  if (!attach?.driveItemId) {
    throw new Error(
      'No PDF on this workstation for that attach. Re-open the label PDF from Smoke Drive or Wave, then Save team attach again.',
    );
  }
  const { blob } = await loadOperatorDriveItemBlob(
    { id: attach.driveItemId, name: attach.fileName, type: 'file' },
    { resolveDownloadUrl: async (item) => getDriveShareLink(item.id) },
  );
  return blob;
}

/**
 * @param {object} order
 * @param {object[]} attaches
 * @returns {Promise<{ blob: Blob, pageCount: number, fileName: string, attachId: string }|null>}
 */
export async function buildShippingLabelPreviewBlobForOrder(order, attaches = []) {
  const hits = findShippingLabelAttachesForOrder(order, attaches);
  if (!hits.length) return null;

  const keys = new Set(collectShipLabelOrderKeys(order));
  const waveKey = getWaveOrderKey(order);
  if (waveKey) keys.add(waveKey);

  // Prefer fully attached docs, then newest-looking id.
  const ranked = [...hits].sort((left, right) => {
    const leftAttached =
      left.status === SHIPPING_LABEL_ATTACH_STATUS.ATTACHED ? 0 : 1;
    const rightAttached =
      right.status === SHIPPING_LABEL_ATTACH_STATUS.ATTACHED ? 0 : 1;
    if (leftAttached !== rightAttached) return leftAttached - rightAttached;
    return String(right.id || '').localeCompare(String(left.id || ''));
  });

  const attach = ranked[0];
  const orderPages = buildOrderKeyToPagesMap(attach);
  const assignments = [];
  orderPages.forEach((pageNumbers, orderKey) => {
    if (!keys.has(String(orderKey || '').replace(/^#/, '').trim())) return;
    pageNumbers.forEach((pageNumber) => {
      assignments.push({ pageNumber, orderKey });
    });
  });
  if (!assignments.length) return null;

  const sourceBlob = await loadAttachSourceBlob(attach);
  const split = await splitShippingLabelPdf(sourceBlob, assignments);
  if (!split.length) {
    throw new Error('Could not split shipping-label pages for this order.');
  }

  // Merge multi-page labels for one order into a single preview PDF when needed.
  if (split.length === 1) {
    return {
      blob: split[0].blob,
      pageCount: 1,
      fileName: attach.fileName || 'shipping-label.pdf',
      attachId: attach.id,
    };
  }

  const { PDFDocument } = await import('pdf-lib');
  const output = await PDFDocument.create();
  for (const part of split) {
    const doc = await PDFDocument.load(await part.blob.arrayBuffer());
    const pages = await output.copyPages(doc, doc.getPageIndices());
    pages.forEach((page) => output.addPage(page));
  }
  const bytes = await output.save({ useObjectStreams: true });
  return {
    blob: new Blob([bytes], { type: 'application/pdf' }),
    pageCount: split.length,
    fileName: attach.fileName || 'shipping-label.pdf',
    attachId: attach.id,
  };
}
